// src/features/map/mapDetail.js
// Live Map "Map detail" (Less / Normal / More): a per-user preference plus a
// pure transform over the two OpenFreeMap vector basemaps (Default = Liberty,
// Dark). Basemap label priority is LAYER ORDER — a symbol layer later in the
// `layers` array is placed first — so "More" moves the quarter / neighbourhood
// layers directly after the village layer instead of using symbol-sort-key.
// Raster and partner styles carry none of these ids and pass through as-is.
//
// MODULE-scope shared state (same shape as src/hooks/useMapLayerPrefs.js):
// the picker writes it, MapView reads it when it sets the style.
// Must not import basemapCatalog.js — that module imports this one.
import { useEffect, useReducer } from 'react';

export const MAP_DETAIL_KEY = 'trackizy_map_detail';
export const MAP_DETAIL_LEVELS = ['less', 'normal', 'more'];
const DEFAULT_LEVEL = 'normal';

function load() {
  try {
    const saved = localStorage.getItem(MAP_DETAIL_KEY);
    return MAP_DETAIL_LEVELS.includes(saved) ? saved : DEFAULT_LEVEL;
  } catch {
    return DEFAULT_LEVEL;
  }
}

let level = load();
const listeners = new Set();
const notify = () => listeners.forEach((l) => l());

export function getMapDetail() {
  return level;
}

/** Unknown values are ignored. */
export function setMapDetail(next) {
  if (!MAP_DETAIL_LEVELS.includes(next)) return;
  level = next;
  try { localStorage.setItem(MAP_DETAIL_KEY, next); } catch { /* ignore */ }
  notify();
}

export default function useMapDetail() {
  const [, force] = useReducer((n) => n + 1, 0);
  useEffect(() => {
    listeners.add(force);
    return () => listeners.delete(force);
  }, []);
  return { level, setLevel: setMapDetail };
}

// ── Transform tables (Liberty ids use dashes/`label_`, Dark uses `place_`) ──

/** Less: raise these layers' minzoom. */
const LESS_MINZOOM = { 'highway-name-major': 14 };
/** Less: hide these layers. */
const LESS_HIDE = new Set([
  'highway-name-minor', 'highway-name-path', 'poi_r1', 'poi_r7', 'poi_r20', // Liberty
  'highway_name_other', // Dark
]);
/** More: village layer → the quarter layers moved directly after it (original relative order kept). */
const MORE_QUARTERS_AFTER = {
  label_village: ['label_other'], // Liberty
  place_village: ['place_other', 'place_suburb'], // Dark
};
const MORE_QUARTER_IDS = new Set(Object.values(MORE_QUARTERS_AFTER).flat());
const MORE_VILLAGE_MINZOOM = 8;
/** More: each POI layer's minzoom drops by one. */
const MORE_POI = new Set(['poi_r1', 'poi_r7', 'poi_r20']);
/** More: quarter labels become single-line, upright, regular weight. */
const MORE_QUARTER_LAYOUT = {
  'text-font': ['Noto Sans Regular'],
  'text-transform': 'none',
  'text-letter-spacing': 0,
  'text-size': ['interpolate', ['linear'], ['zoom'], 11, 11, 12, 12, 15, 14],
  'text-max-width': 12,
};

/** Single-line quarter name: local script first for Arabic / Kurdish UIs, Latin first otherwise. */
function quarterTextField(lang) {
  return /^(ar|ckb)/.test(String(lang || ''))
    ? ['coalesce', ['get', 'name:nonlatin'], ['get', 'name'], ['get', 'name:latin']]
    : ['coalesce', ['get', 'name:latin'], ['get', 'name']];
}

const KNOWN_IDS = new Set([
  ...Object.keys(LESS_MINZOOM),
  ...LESS_HIDE,
  ...Object.keys(MORE_QUARTERS_AFTER),
  ...MORE_QUARTER_IDS,
  ...MORE_POI,
]);

function lessLayer(layer) {
  if (LESS_MINZOOM[layer.id] != null) return { ...layer, minzoom: LESS_MINZOOM[layer.id] };
  if (LESS_HIDE.has(layer.id)) return { ...layer, layout: { ...layer.layout, visibility: 'none' } };
  return layer;
}

function moreLayer(layer, lang) {
  if (MORE_QUARTER_IDS.has(layer.id)) {
    // Restyle only: the layer keeps its own minzoom (Liberty 8, Dark unset) so
    // names that show at zoom 8–10 today keep showing.
    return {
      ...layer,
      layout: { ...layer.layout, ...MORE_QUARTER_LAYOUT, 'text-field': quarterTextField(lang) },
    };
  }
  if (MORE_QUARTERS_AFTER[layer.id]) {
    // Lower only, never raise; a layer with no minzoom (Dark) already starts at 0.
    if (typeof layer.minzoom !== 'number') return layer;
    return { ...layer, minzoom: Math.min(layer.minzoom, MORE_VILLAGE_MINZOOM) };
  }
  if (MORE_POI.has(layer.id) && typeof layer.minzoom === 'number') return { ...layer, minzoom: layer.minzoom - 1 };
  return layer;
}

/** Move each village's quarter layers to directly after it, so they are placed first. */
function reorderQuarters(layers) {
  const present = new Set(layers.map((l) => l.id));
  const moving = new Set();
  for (const [village, quarters] of Object.entries(MORE_QUARTERS_AFTER)) {
    if (present.has(village)) quarters.forEach((q) => present.has(q) && moving.add(q));
  }
  if (moving.size === 0) return layers;
  const byId = new Map(layers.map((l) => [l.id, l]));
  const out = [];
  for (const layer of layers) {
    if (moving.has(layer.id)) continue;
    out.push(layer);
    for (const q of MORE_QUARTERS_AFTER[layer.id] || []) {
      if (moving.has(q)) out.push(byId.get(q));
    }
  }
  return out;
}

/**
 * Apply a Map detail level to a basemap style. Returns a new style object and
 * never mutates the input; `normal`, unknown levels and styles carrying none
 * of the known layer ids (raster, partner) come back as the same reference.
 * `lang` (UI language) picks the script of More's single-line quarter names.
 */
export function applyMapDetail(style, detailLevel, { lang } = {}) {
  const layers = style?.layers;
  if (!Array.isArray(layers) || detailLevel === 'normal') return style;
  if (detailLevel !== 'less' && detailLevel !== 'more') return style;
  if (!layers.some((l) => KNOWN_IDS.has(l?.id))) return style;
  if (detailLevel === 'less') return { ...style, layers: layers.map(lessLayer) };
  return { ...style, layers: reorderQuarters(layers.map((l) => moreLayer(l, lang))) };
}

/** True for the vector basemaps Map detail applies to (they carry a `place` symbol layer). */
export function isVectorBasemap(style) {
  return Array.isArray(style?.layers)
    && style.layers.some((l) => l?.type === 'symbol' && l['source-layer'] === 'place');
}
